import { useClient } from './ClientContext';
import { useGlobals } from './GlobalContext';
import { CartItems, useCart } from './CartContext';
import { useDiscountFlex } from './DiscountFlexContext';
import { useMemo, useContext, createContext, useCallback } from 'react';
import { useUserContext } from './userContext/UserContext'; // <== novo
import { getOnStorage } from '@/shared';

interface HookProps {
  // Consumo (apenas DESCONTOS: percentuais negativos) -> impacta saldo ERP
  discountAmount: number;
  discountAmountClient: number;
  getDiscountAmountByClient: (client: number) => number;

  // Novos: valores para exibição no carrinho
  increasesClient: number; // Somente acréscimos (saldo a faturar) do cliente atual
  totalFlexClient: number; // discountAmountClient (negativo) + increasesClient (positivo)
  discountAmountAll: number; // Para todos os clientes (caso seja preciso em outro lugar)

  // Novos: saldos estimados (não persistidos)
  estimatedSaldoFlex: number; // saldo base + descontos (todos clientes)
  estimatedSaldoFlexClient: number; // saldo base + descontos cliente atual

  // Novos aliases (usados para leitura mais semântica em outros componentes)
  currentSaldoFlex?: number;
  currentSaldoFlexClient?: number;
}

const DiscountFlexAmountContext = createContext<HookProps>({} as HookProps);

const DiscountFlexAmountProvider: React.FC<any> = ({ children }) => {
  const { cartDetails } = useCart();
  const { paymentCode } = useGlobals();
  const { selectedClient } = useClient();
  const { discounts } = useDiscountFlex();
  const { rcaSaldoFlex } = useUserContext(); // saldo base persistido (ainda sem consumo do carrinho)
  const baseSaldo = rcaSaldoFlex || 0;

  // Recupera preço original a partir do final + percentual negativo
  const getOriginalValue = (finalValue: number, percent: number) => {
    if (percent >= 0) return finalValue; // acréscimo ou neutro não reconstrói “desconto”
    const d = percent / 100;
    const fator = 1 + d;
    if (fator <= 0) return finalValue;
    return finalValue / fator;
  };

  // Mapa (client-uid) -> CartItem (para multiplicar por quantidade no carrinho)
  const mapCartItems = useMemo(() => {
    if (!cartDetails) return {};
    return Object.keys(cartDetails).reduce(
      (acc, client) => {
        const items = cartDetails[Number(client)].items.reduce((ac, p) => ({ ...ac, [`${client}-${p.uid}`]: p }), {});
        return { ...acc, ...items };
      },
      {} as { [key: string]: CartItems }
    );
  }, [cartDetails]);

  // Consumo total de FLEX (apenas DESCONTOS) em todos os clientes
  const discountAmount = useMemo(() => {
    if (!discounts) return 0;

    const paymentCodes = getOnStorage('paymentCode') || {};

    const totalDiscount = Object.keys(discounts).reduce((acc, clientCod) => {
      const client = discounts[Number(clientCod)];
      const codePayment = paymentCodes[Number(clientCod)];
      return (
        acc +
        Object.keys(client).reduce((accDiscount, uid) => {
          const entry: any = client[uid];
          const percentual = Number(entry?.percentual || 0);

          if (percentual >= 0) return accDiscount; // ignora acréscimos

          const finalPrice = Number(entry?.discountPrice?.[codePayment] || 0);
          if (!finalPrice) return accDiscount;

          const originalPrice = getOriginalValue(finalPrice, percentual);
          const multiple = mapCartItems?.[`${clientCod}-${uid}`]?.quantity || 0;

          const diff = (finalPrice - originalPrice) * multiple; // negativo
          return accDiscount + diff;
        }, 0)
      );
    }, 0);

    return Number(totalDiscount.toFixed(2));
  }, [discounts, mapCartItems]);

  // Acréscimos (saldoAFaturar) do cliente atual (sempre positivo)
  // saldoAFaturar armazenado POR PACK -> multiplicar pela quantidade no carrinho
  const increasesClient = useMemo(() => {
    const clientCod = selectedClient?.COD_CLIENTE;
    if (!clientCod) return 0;
    const client = discounts?.[clientCod] || {};

    const totalInc = Object.keys(client).reduce((acc, uid) => {
      const entry: any = client[uid];
      const percentual = Number(entry?.percentual || 0);
      const saldoAFaturar = Number(entry?.saldoAFaturar || 0);
      const multiple = mapCartItems?.[`${clientCod}-${uid}`]?.quantity || 0;

      // Prioridade:
      // 1. Se saldoAFaturar > 0, usamos (saldoAFaturar * quantidade)
      // 2. Se não houver saldoAFaturar mas percentual > 0, calculamos acréscimo via percentual (fallback)
      if (saldoAFaturar > 0) {
        return acc + saldoAFaturar * multiple;
      }

      if (percentual > 0) {
        const finalPrice = Number(entry?.discountPrice?.[paymentCode] || 0);
        if (!finalPrice) return acc;
        // originalPrice = finalPrice / (1 + percentual/100)
        const fator = 1 + percentual / 100;
        if (fator <= 0) return acc;
        const originalPrice = finalPrice / fator;
        const diff = (finalPrice - originalPrice) * multiple;
        return acc + diff;
      }

      return acc;
    }, 0);

    return Number(totalInc.toFixed(2));
  }, [discounts, paymentCode, mapCartItems, selectedClient?.COD_CLIENTE]);

  const getDiscountAmountByClient = useCallback(
    (clientCod: number) => {
      if (!clientCod || !discounts) return 0;
      const client = discounts?.[clientCod] || {};
      const totalDiscount = Object.keys(client).reduce((accDiscount, uid) => {
        const entry: any = client[uid];
        const percentual = Number(entry?.percentual || 0);
        if (percentual >= 0) return accDiscount;
        const finalPrice = Number(entry?.discountPrice?.[paymentCode] || 0);
        if (!finalPrice) return accDiscount;
        const originalPrice = getOriginalValue(finalPrice, percentual);
        const multiple = mapCartItems?.[`${clientCod}-${uid}`]?.quantity || 0;
        const diff = (finalPrice - originalPrice) * multiple;
        return accDiscount + diff;
      }, 0);
      return Number(totalDiscount.toFixed(2));
    },
    [discounts, paymentCode, mapCartItems]
  );

  const discountAmountAll = useMemo(() => {
    return discountAmount;
  }, [discountAmount]);

  const discountAmountClient = useMemo(() => {
    const clientCod = selectedClient?.COD_CLIENTE;
    if (!clientCod) return 0;
    const client = discounts?.[clientCod] || {};

    const totalDiscount = Object.keys(client).reduce((accDiscount, uid) => {
      const entry: any = client[uid];
      const percentual = Number(entry?.percentual || 0);
      if (percentual >= 0) return accDiscount;

      const finalPrice = Number(entry?.discountPrice?.[paymentCode] || 0);
      if (!finalPrice) return accDiscount;

      const originalPrice = getOriginalValue(finalPrice, percentual);
      const multiple = mapCartItems?.[`${clientCod}-${uid}`]?.quantity || 0;
      const diff = (finalPrice - originalPrice) * multiple; // negativo
      return accDiscount + diff;
    }, 0);

    return Number(totalDiscount.toFixed(2));
  }, [discountAmount]);

  const estimatedSaldoFlex = useMemo(() => Number((baseSaldo + discountAmount).toFixed(2)), [baseSaldo, discountAmount]);

  const estimatedSaldoFlexClient = useMemo(
    () => Number((baseSaldo + discountAmountClient).toFixed(2)),
    [baseSaldo, discountAmountClient]
  );

  const totalFlexClient = useMemo(
    () => Number((discountAmountClient + increasesClient).toFixed(2)),
    [discountAmountClient, increasesClient]
  );

  return (
    <DiscountFlexAmountContext.Provider
      value={{
        discountAmount, // só descontos (negativo)
        discountAmountClient, // só descontos (negativo)
        getDiscountAmountByClient,
        increasesClient, // só acréscimos
        totalFlexClient, // descontos + acréscimos (para exibir no carrinho)
        discountAmountAll, // agregação geral
        estimatedSaldoFlex,
        estimatedSaldoFlexClient,
        // aliases
        currentSaldoFlex: estimatedSaldoFlex,
        currentSaldoFlexClient: estimatedSaldoFlexClient,
      }}
    >
      {children}
    </DiscountFlexAmountContext.Provider>
  );
};

const useDiscountAmountFlex = (): HookProps & {
  increasesClient: number;
  totalFlexClient: number;
  discountAmountAll: number;
} => {
  const ctx = useContext(DiscountFlexAmountContext);
  if (!ctx) throw new Error('Erro ao usar hook DiscountFlexAmount');
  return ctx;
};

export { DiscountFlexAmountProvider, useDiscountAmountFlex };
